import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'localization';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import InputAtom from '../../atoms/Input/InputAtom';
import TextareaAtom from '../../atoms/Textarea/TextareaAtom';
import './AddLinkFormPart.scss';

export interface AddLinkFormSubmitPayload {
  url: string;
  name?: string;
  description?: string;
}

export interface AddLinkFormInitialValues {
  url?: string;
  name?: string;
  description?: string | null;
}

export type AddLinkFormMode = 'create' | 'edit';

export interface AddLinkFormPartProps {
  mode?: AddLinkFormMode;
  initialValues?: AddLinkFormInitialValues;
  onSubmit: (payload: AddLinkFormSubmitPayload) => Promise<unknown>;
  onCancel: () => void;
  isSubmitting?: boolean;
  className?: string;
}

type UrlValidation =
  | { kind: 'ok'; url: string }
  | { kind: 'empty' }
  | { kind: 'invalid' }
  | { kind: 'scheme' };

function validateUrl(raw: string): UrlValidation {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { kind: 'empty' };
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return { kind: 'invalid' };
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return { kind: 'scheme' };
  }
  return { kind: 'ok', url: trimmed };
}

const AddLinkFormPart: React.FC<AddLinkFormPartProps> = ({
  mode = 'create',
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting: isSubmittingProp = false,
  className = '',
}) => {
  const { t } = useTranslation();

  const [url, setUrl] = useState(() => initialValues?.url ?? '');
  const [name, setName] = useState(() => initialValues?.name ?? '');
  const [description, setDescription] = useState(
    () => initialValues?.description ?? '',
  );
  const [error, setError] = useState<string | null>(null);
  const [internalSubmitting, setInternalSubmitting] = useState(false);

  const urlInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    urlInputRef.current?.focus();
  }, []);

  const isSubmitting = internalSubmitting || isSubmittingProp;
  const interactive = !isSubmitting;

  const handleCancel = useCallback(() => {
    if (internalSubmitting) return;
    setError(null);
    onCancel();
  }, [internalSubmitting, onCancel]);

  const resolveValidationError = useCallback(
    (validation: UrlValidation): string | null => {
      if (validation.kind === 'empty') return t('attachments.errors.urlRequired');
      if (validation.kind === 'invalid') return t('attachments.errors.linkInvalidUrl');
      if (validation.kind === 'scheme')
        return t('attachments.errors.linkSchemeNotAllowed');
      return null;
    },
    [t],
  );

  const handleSubmit = useCallback(
    async (event?: { preventDefault?: () => void; stopPropagation?: () => void }) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      if (!interactive) return;

      const validation = validateUrl(url);
      const validationError = resolveValidationError(validation);
      if (validationError || validation.kind !== 'ok') {
        setError(validationError);
        return;
      }

      const trimmedName = name.trim();
      const trimmedDescription = description.trim();

      setInternalSubmitting(true);
      setError(null);
      try {
        await onSubmit({
          url: validation.url,
          name: trimmedName.length > 0 ? trimmedName : undefined,
          description: trimmedDescription.length > 0 ? trimmedDescription : undefined,
        });
      } catch (err: unknown) {
        const message =
          err instanceof Error && err.message
            ? err.message
            : t('attachments.uploadFailed');
        setError(message);
      } finally {
        setInternalSubmitting(false);
      }
    },
    [description, interactive, name, onSubmit, resolveValidationError, t, url],
  );

  const rootClasses = ['add-link-form-part', className].filter(Boolean).join(' ');

  // Render as a <fieldset> rather than a <form>. The form-part is also rendered
  // inline inside SphereEditor's Formik <Form>, and HTML disallows nested
  // <form> elements — browsers silently drop the inner one and the submit
  // button binds to the outer form's onSubmit (which is the sphere-save
  // handler). <fieldset>/<legend> provides proper grouping semantics for
  // assistive tech without nesting forms.
  const formRef = useRef<HTMLFieldSetElement | null>(null);
  // Document-level Enter handler so the fieldset itself doesn't carry a
  // keyboard event listener (Sonar S6847). Scoped to keys originating inside
  // this form via the ref check; ignored for textarea so newlines still work.
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Enter' || event.shiftKey) return;
      const target = event.target as HTMLElement | null;
      if (!target || !formRef.current?.contains(target)) return;
      if (target.tagName === 'TEXTAREA') return;
      event.preventDefault();
      event.stopPropagation();
      handleSubmit().catch(() => undefined);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handleSubmit]);

  const formTitle = t(
    mode === 'edit'
      ? 'attachments.editLinkDialog.title'
      : 'attachments.addLinkDialog.title',
  );

  return (
    <fieldset ref={formRef} className={rootClasses}>
      <legend className="add-link-form-part__sr-only">{formTitle}</legend>
      <div className="add-link-form-part__field">
        <label
          htmlFor="add-link-form-url"
          className="add-link-form-part__label"
        >
          {t('attachments.labels.url')}
        </label>
        <InputAtom
          ref={urlInputRef}
          id="add-link-form-url"
          type="url"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          placeholder={t('attachments.placeholders.url')}
          disabled={!interactive}
          aria-invalid={error ? true : undefined}
          data-testid="add-link-form-url"
        />
      </div>

      <div className="add-link-form-part__field">
        <label
          htmlFor="add-link-form-name"
          className="add-link-form-part__label"
        >
          {t('attachments.labels.name')}
        </label>
        <InputAtom
          id="add-link-form-name"
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('attachments.placeholders.name')}
          disabled={!interactive}
          data-testid="add-link-form-name"
        />
      </div>

      <div className="add-link-form-part__field">
        <label
          htmlFor="add-link-form-description"
          className="add-link-form-part__label"
        >
          {t('attachments.labels.description')}
        </label>
        <TextareaAtom
          id="add-link-form-description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder={t('attachments.placeholders.description')}
          rows={3}
          disabled={!interactive}
        />
      </div>

      {error && (
        <div className="add-link-form-part__error" role="alert">
          <i className="pi pi-exclamation-circle" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      <div className="add-link-form-part__actions">
        <ButtonAtom
          variant="text"
          label={t('attachments.addLinkDialog.cancel')}
          onClick={handleCancel}
          disabled={internalSubmitting}
          type="button"
        />
        <ButtonAtom
          variant="primary"
          icon={internalSubmitting ? 'pi pi-spin pi-spinner' : 'pi pi-link'}
          label={t(
            mode === 'edit'
              ? 'attachments.editLinkDialog.submit'
              : 'attachments.addLinkDialog.submit',
          )}
          disabled={!interactive}
          type="button"
          onClick={() => {
            handleSubmit().catch(() => undefined);
          }}
        />
      </div>
    </fieldset>
  );
};

export default AddLinkFormPart;
